import React from "react";
import { Drawer, Schema, Icon, IconButton } from "rsuite";
import { injectIntl } from "react-intl";
import styled from "styled-components";

import {
    LS_DIALER_TRAFFIC_STAT_COLUMNS,
    LS_DIALER_TRAFFIC_STAT_SMS_PER_PAGE,
    LS_DIALER_TRAFFIC_STAT_VOICE_PER_PAGE
} from "const/localStorageKeys";
import {
    LOCAL_STORAGE_TRAFFIC_STAT_FILTERS_DIALER,
    MAX_PER_PAGE_DIALER,
    MIN_PER_PAGE_DIALER,
    REPORT_DEFAULT_PER_PAGE
} from "const";

import { removeTZFromDate, toUTCDateTime } from "utils";
import { Spacer } from "components/base/Spacer";
import { ButtonPrimary } from "components/base/BaseButton";
import ButtonDownload from "components/client/ButtonDownload";
import TrafficStatFilters from "./TrafficStatFilters";
import TrafficStatTable from "./TrafficStatTable";
import m from "definedMessages";
import {
    FilterSectionFlexboxGrid,
    StyledItemFitWidth,
    StyledItemAutoMargin
} from "components/base/FitContainers";
import { useWindowWidth } from "hooks";
import { withTranslation } from "hoc/WithTranslation";


// наш узагальнений хук
import useReportPage from "../useReportPage";

const { StringType } = Schema.Types;
const MAX_CHARACTERS = 40;

/**
 * Рахує кількість активних фільтрів (логіка як у твоєму TrafficStat)
 * - 1 за дату
 * - +1 якщо набір checkbox відрізняється від "базового" (умовно — довжина !== 3)
 * - +1 якщо заповнений a_number
 */
const calcActiveFiltersTrafficStat = (filter) => {
    let active = 0;
    active = (filter?.checkbox?.length !== 5) ? active + 1 : active;
    active = (filter?.a_number || filter?.senderid) ? active + 1 : active;
    return active + 1; // +1 за діапазон дат
};

/**
 * Валідація фільтра TrafficStat
 */
const makeTrafficFiltersModel = (formatMessage) => {
    return Schema.Model({
        a_number: StringType()
            .pattern(/^\d+$/, formatMessage(m.correctNumber))
            .maxLength(MAX_CHARACTERS, formatMessage(m.maxLength, { count: MAX_CHARACTERS }))
    });
};

const TrafficStatPage = ({
    type,
    page,
    count,
    loading,
    columns,            // повний список колонок (із пропсів вище по дереву)
    service,            // true (voice) / false (sms) / null
    prevType,
    summaries,
    trafficStat,
    dateTimeState,
    uploadLoading,
    globalSettings,
    subdestinationList,

    // екшени
    exportReport: exportReportProp,
    getTrafficStat,
    setDateTimeState,

    formatMessage
}) => {
    const resizedWidth = useWindowWidth();
    const isMobile = resizedWidth < 768;
    const perPageLSKey = service === false
        ? LS_DIALER_TRAFFIC_STAT_SMS_PER_PAGE
        : LS_DIALER_TRAFFIC_STAT_VOICE_PER_PAGE;

    // === Конфіг для хука useReportPage ===

    // дефолтні ключі колонок, які перемикаються чекбоксами
    const DEFAULT_COLUMNS_KEYS = ["range", "a_number", "senderid", "b_number", "phone", "destination", "b_destination", "month"];

    // дефолтні фільтри для voice/sms
    const buildDefaultFilter = (svc, range, saved) => {
        if (saved) return { ...saved };
        const base = {
            range,
            start_date: removeTZFromDate(range[0]),
            end_date: removeTZFromDate(range[1])
        };
        if (svc) {
            // voice
            return {
                ...base,
                group: "range/a_number/b_number/b_destination/month",
                checkbox: ["range", "a_number", "b_number", "b_destination", "month"]
            };
        }
        // sms
        return {
            ...base,
            group: "range/senderid/phone/destination/month",
            checkbox: ["range", "senderid", "phone", "destination", "month"]
        };
    };

    const normalizeGroupFromCheckbox = (arr) =>
        Array.isArray(arr) ? arr.join("/") : "";

    // при зміні service (voice/sms) — адаптуємо ключі у group/checkbox та полях
    const adaptFilterByService = (svc, f) => {
        if (!f) return f;
        const next = { ...f };

        const toVoice = { senderid: "a_number", phone: "b_number", destination: "b_destination" };
        const toSms = { a_number: "senderid", b_number: "phone", b_destination: "destination" };

        const mapCheckbox = (arr = [], map) => {
            const out = [];
            for (const k of arr) out.push(map[k] || k);

            return out.filter((v, i) => out.indexOf(v) === i);
        };


        const renameField = (obj, from, to) => {
            if (Object.prototype.hasOwnProperty.call(obj, from)) {
                obj[to] = obj[from];
                delete obj[from];
            }
        };

        if (svc === true) {
            next.checkbox = mapCheckbox(next.checkbox, toVoice);
            renameField(next, "senderid", "a_number");
            renameField(next, "phone", "b_number");
            renameField(next, "destination", "b_destination");

        } else if (svc === false) {
            next.checkbox = mapCheckbox(next.checkbox, toSms);
            renameField(next, "a_number", "senderid");
            renameField(next, "b_number", "phone");
            renameField(next, "b_destination", "destination");
        }

        next.group = normalizeGroupFromCheckbox(next.checkbox);

        return next;
    };

    // валідація
    const filtersModel = makeTrafficFiltersModel(formatMessage);
    const validateFilter = (f) => {
        const checker = filtersModel.check(f || {});
        return Object.keys(checker).some((k) => checker[k].hasError);
    };

    // формування summary-рядка (як у твоєму коді)
    const composeSummaryRow = (templateRow, s) => {
        const row = { ...templateRow };

        Object.keys(row).forEach((key) => {
            switch (key) {
                case "sum_cost_dialer":
                    row[key] = s?.cost ?? null;
                    break;
                case "count_cost_dialer":
                    row[key] = s?.calls ?? null;
                    break;
                case "sum_duration":
                    row[key] = s?.duration ?? null;
                    break;
                case "sum_rate_dialer":
                    row[key] = s?.cost ?? null;
                    break;
                case "count_rate_dialer":
                    row[key] = s?.messages ?? null;
                    break;
                case "id":
                    row[key] = "summaries";
                    break;
                default:
                    row[key] = s?.[key] ?? null;
            }
        });
        return row;
    };

    const trafficStatConfig = {
        storageKeys: {
            perPage: perPageLSKey,
            columns: LS_DIALER_TRAFFIC_STAT_COLUMNS,
            filters: LOCAL_STORAGE_TRAFFIC_STAT_FILTERS_DIALER
        },
        limits: { minPerPage: MIN_PER_PAGE_DIALER, maxPerPage: MAX_PER_PAGE_DIALER },
        columnsAll: columns,
        defaultColumnsKeys: DEFAULT_COLUMNS_KEYS,
        visibleKeysForService: (svc) =>
            svc
                ? ["range", "a_number", "b_number", "b_destination", "month"]
                : ["range", "senderid", "phone", "destination", "month"],

        buildDefaultFilter: (svc, dt, saved) => buildDefaultFilter(svc, dt, saved),
        adaptFilterByService: (svc, f) => adaptFilterByService(svc, f),
        validateFilter: (f) => validateFilter(f),
        composeSummaryRow: (rowTemplate, s) => composeSummaryRow(rowTemplate, s),

        fetchList: getTrafficStat,
        exportReport: exportReportProp,
        toUTC: toUTCDateTime,

        // динамічні вхідні дані:
        service,
        type,
        prevType,
        perPageProp: REPORT_DEFAULT_PER_PAGE,
        dateTimeState,
        dataList: trafficStat,
        summaries,

        // як рахувати активні фільтри
        calcActiveFilters: calcActiveFiltersTrafficStat
    };

    const { state, actions } = useReportPage(trafficStatConfig);

    const {
        filter,
        shownColumns,
        perPage,
        savedTableColumns,
        activeFiltersCount,
        showFilters,
        dataWithSummaries
    } = state;

    const {
        setShowFilters,
        onChangeFilters,
        getItems,
        onSaveColumnsTable,
        applyFilterAndFetch,
        exportNow
    } = actions;

    return (
        <>
            {isMobile && (
                <>
                    <ButtonApplyFilterMobile
                        onClick={() => applyFilterAndFetch(filter, 1)}
                    >
                        {formatMessage(m.applyFilter)}
                    </ButtonApplyFilterMobile>
                    <Toggler
                        icon={<Icon icon="filter" />}
                        onClick={() => setShowFilters(true)}
                    >
                        {activeFiltersCount}
                    </Toggler>
                    <Drawer
                        size="xs"
                        placement="bottom"
                        style={{ height: "260px" }}
                        show={showFilters}
                        onHide={() => setShowFilters(false)}
                    >
                        <Drawer.Header />
                        <DrawerBody>
                            <TrafficStatFilters
                                onChangeFilters={(f) => onChangeFilters(f)} // під капотом хук сам порахує activeFilters
                                loading={loading}
                                filtersModel={filtersModel}
                                {...{
                                    filter,
                                    service,
                                    globalSettings,
                                    subdestinationList,
                                    dateTimeState,
                                    setDateTimeState
                                }}
                                mobile
                            />
                        </DrawerBody>
                    </Drawer>
                </>
            )}

            <Spacer size={30} />

            {!isMobile && (
                <>
                    <FilterSectionFlexboxGrid>
                        <StyledItemFitWidth>
                            <TrafficStatFilters
                                onChangeFilters={(f) => onChangeFilters(f)}
                                loading={loading}
                                filtersModel={filtersModel}
                                {...{
                                    filter,
                                    service,
                                    globalSettings,
                                    subdestinationList,
                                    dateTimeState,
                                    setDateTimeState
                                }}
                            />
                        </StyledItemFitWidth>
                        <StyledItemAutoMargin>
                            <ButtonApplyFilter
                                onClick={() => applyFilterAndFetch(filter, 1)}
                            >
                                {formatMessage(m.applyFilter)}
                            </ButtonApplyFilter>
                            <ButtonDownload
                                onDownload={() => exportNow(page)}
                                limitWidth={875}
                                buttonText={formatMessage(m.exportReport)}
                                loading={uploadLoading}
                            />
                        </StyledItemAutoMargin>
                    </FilterSectionFlexboxGrid>

                    <Spacer size={19} />
                </>
            )}

            <Spacer size={1} />

            <TrafficStatTable
                data={dataWithSummaries || trafficStat}
                extraRows={(dataWithSummaries && dataWithSummaries.length > (trafficStat?.length || 0)) ? 1 : 0}
                availableColumns={columns}
                pickedColumns={shownColumns}
                loading={loading}
                {...{
                    getItems,
                    type,
                    page,
                    perPage,
                    perPageLSKey,
                    count,
                    service,
                    savedTableColumns,
                    onSaveColumnsTable
                }}
            />

            {isMobile && (
                <StyledActionButtonWrapper>
                    <ButtonDownload
                        onDownload={() => exportNow(page)}
                        limitWidth={875}
                        buttonText={formatMessage(m.exportReport)}
                        loading={uploadLoading}
                    />
                </StyledActionButtonWrapper>
            )}
        </>
    );
};

export default withTranslation(TrafficStatPage);

const Toggler = styled(IconButton)`
    && {
        color: #363434;
        float: right;
        margin-top: 6px;
    }
`;

const ButtonApplyFilter = styled(ButtonPrimary)`
    && {
        margin-right: 20px;
    }
`;

const StyledActionButtonWrapper = styled.div`
    padding-top: 15px;
    text-align: center;

    @media (min-width: 768px) {
        text-align: right;
    }
`;

const DrawerBody = styled(Drawer.Body)`
    && {
        position: initial;
    }
    
    && .dateTimePicker {
        height: 40px;
    }
    && .dateTimePicker.dateFrom {
        margin-top: -485px;
        position: absolute;
    }
    && .dateTimePicker.dateTo {
        margin-top: -165px;
        position: absolute;
    }
    && .dateTimePicker .dateTimeName {
        color: var(--color-light);
        font-weight: 600;
        background-color: rgba(0, 0, 0, 0.4);
        padding: 10px;
    }
`;

const ButtonApplyFilterMobile = styled(ButtonPrimary)`
    && {
        position: absolute;
        right: 100px;
    }
`;
